import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/app.css';
import App from './App';

// AGGRESSIVE CACHE BUSTING - Clear everything IMMEDIATELY on load
// This ensures hard refresh always works and old content is never shown
(function clearAllCachesImmediately() {
  console.log('🗑️ AGGRESSIVE CACHE CLEARING - Starting...');
  
  // 1. Unregister ALL service workers immediately
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister().then(() => {
          console.log('🗑️ Service worker unregistered');
        }).catch(() => {});
      });
    }).catch(() => {});
  }
  
  // 2. Clear ALL caches immediately
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName).then(() => {
          console.log(`🗑️ Cache deleted: ${cacheName}`);
        }).catch(() => {});
      });
    }).catch(() => {});
  }
  
  // 3. Clear session storage
  try {
    const sessionAstro = sessionStorage.getItem('fr00t_astrological_data');
    sessionStorage.clear();
    if (sessionAstro) {
      sessionStorage.setItem('fr00t_astrological_data', sessionAstro);
    }
    console.log('🗑️ Session storage cleared (astrological profile preserved)');
  } catch (e) {}
  
  // 4. Check if this is a reload and clear more aggressively
  const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navEntry?.type === 'reload' || performance.navigation?.type === 1) {
    console.log('🔄 Reload detected - clearing localStorage too');
    try {
      // Only clear non-essential localStorage (keep user data)
      const keysToKeep = ['sacred-spiritual-store', 'fr00t_astrological_data']; // Keep user + astrology data
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {}
  }
  
  // 5. Force reload if URL has refresh param
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('_refresh') || urlParams.has('t')) {
    // Remove the refresh param to avoid infinite loop
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }
  
  console.log('✅ Cache clearing complete');
})();

// Initialize Fr00t Platform
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Fr00t numerological foundation: F(6) + R(9) + 0(0) + 0(0) + T(2) = 17 → 8
const BUILD_VERSION = `v${Date.now()}`;
console.log('🍇 Fr00t Platform Initializing');
console.log('Harmony • Wisdom • Infinite Potential • Partnership • Authority');
console.log(`🔄 Build version: ${BUILD_VERSION}`);
console.log(`🔄 Build timestamp: ${new Date().toISOString()}`);

// Store version to detect stale loads
if (typeof window !== 'undefined') {
  const storedVersion = sessionStorage.getItem('fr00t_build_version');
  if (storedVersion && storedVersion !== BUILD_VERSION) {
    console.warn('⚠️ STALE BUILD DETECTED - Forcing reload...');
    sessionStorage.setItem('fr00t_build_version', BUILD_VERSION);
    window.location.reload();
    throw new Error('Stale build - reloading');
  }
  sessionStorage.setItem('fr00t_build_version', BUILD_VERSION);
}

// Hot Module Replacement (HMR) Support
// IMPORTANT: Manual refreshes (F5, Cmd+R, Ctrl+R, Cmd+Shift+R) ALWAYS work
// HMR only handles automatic updates during development - never blocks manual refresh
if (process.env.NODE_ENV === 'development') {
  // Enable HMR for React components (non-blocking)
  if (typeof module !== 'undefined' && module.hot) {
    // Accept updates to App component
    module.hot.accept('./App', () => {
      console.log('🔄 Hot Module Replacement: App updated');
    });

    // Only reload on compilation errors - never interfere with manual refresh
    module.hot.addStatusHandler((status) => {
      if (status === 'abort' || status === 'fail') {
        console.log('🔄 HMR compilation failed, reloading page...');
        window.location.reload();
      }
    });

    console.log('🔄 HMR enabled - manual refresh always works');
  }
}

// AGGRESSIVE CACHE CLEARING ON HARD REFRESH
// This ensures hard refresh (Cmd+Shift+R / Ctrl+Shift+R) always clears everything
window.addEventListener('keydown', (e) => {
  // Cmd+Shift+R / Ctrl+Shift+R - hard refresh (bypass cache)
  if (e.key === 'R' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
    console.log('🔄 HARD REFRESH - Clearing EVERYTHING...');
    
    // Clear ALL storage immediately
    const sessionAstro = sessionStorage.getItem('fr00t_astrological_data');
    sessionStorage.clear();
    if (sessionAstro) {
      sessionStorage.setItem('fr00t_astrological_data', sessionAstro);
    }
    // Don't clear user data, but clear everything else
    const userData = localStorage.getItem('sacred-spiritual-store');
    const astroData = localStorage.getItem('fr00t_astrological_data');
    localStorage.clear();
    if (userData) {
      localStorage.setItem('sacred-spiritual-store', userData);
    }
    if (astroData) {
      localStorage.setItem('fr00t_astrological_data', astroData);
    }
    
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }
    
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName);
        });
      });
    }
    
    // Force reload with cache bypass - use location.reload(true) equivalent
    window.location.href = window.location.pathname + '?hard_refresh=' + Date.now();
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
  // F5 or Cmd+R / Ctrl+R - normal refresh with cache bust
  if (e.key === 'F5' || (e.key === 'r' && (e.metaKey || e.ctrlKey) && !e.shiftKey)) {
    // Add cache-busting query param
    const url = new URL(window.location.href);
    url.searchParams.set('_refresh', Date.now().toString());
    window.location.href = url.toString();
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
}, { capture: true });

// Render application
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

